import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';

import { fetchChatbotQuery } from 'services/fetchChatbotQuery';
import { useAppConfig } from 'state/appConfig';
import { useChatbotQueryContext } from 'state/chatbotQueryResponseState';

const CHATBOT_QUERY_KEY = 'chatbotQuery';

const getUserCountryCode = (countryCode) =>
  !!countryCode ? { UserCountryCode: countryCode } : {};

/**
 * useChatbotInitialContextQuery
 * @description Chatbot initial context query
 * @param {Object} configuration
 * @param {boolean} configuration.enabled an exposed flag to control wheter the query should be enabled
 */
export const useChatbotInitialContextQuery = ({ enabled = true }) => {
  const {
    chatbotBusinessArea: businessArea,
    chatbotChannel: channel,
    chatbotUrl,
    countryCode,
  } = useAppConfig();
  const { hasQueryContext } = useChatbotQueryContext();

  return useQuery({
    queryKey: [CHATBOT_QUERY_KEY],
    queryFn: () =>
      fetchChatbotQuery(chatbotUrl, {
        init: 1,
        businessArea,
        Channel: channel,
        ...getUserCountryCode(countryCode),
      }),

    /*
     * Because this initial query is important and no other way to re-trigger fetching,
     * so adding a few retries here. And also, this is a get request, it is unlikely to
     * be 4xx client side error. So, try 3 times for whatever error should be ok.
     */
    retry: 3,
    staleTime: Infinity,
    enabled: !!chatbotUrl && !hasQueryContext && enabled,
  });
};

/**
 * useChatbotQuery
 * @description return expose a "fetchChatQuery" callback, so it can manullay trigger entry fetching,
 * instead of using "refetch" from "useQuery", which can't accept parameters.
 *
 * @param {Object} queryConfig
 * @returns {Object}
 */
export const useChatbotQuery = () => {
  const [query, setQuery] = useState({});

  const { chatbotUrl, countryCode } = useAppConfig();

  const { queryContext, hasQueryContext } = useChatbotQueryContext();

  const isFetchEnabled =
    [chatbotUrl, hasQueryContext].every(Boolean) && !isEmpty(query);

  const chatbotQuery = useQuery({
    queryKey: [CHATBOT_QUERY_KEY, query],
    queryFn: () =>
      fetchChatbotQuery(chatbotUrl, {
        ...queryContext,
        ...query,
        ...getUserCountryCode(countryCode),
      }),
    enabled: isFetchEnabled,
    cacheTime: 0,
  });

  const sendChatbotQuery = useCallback(
    (query) => {
      setQuery({
        ...query,
        timestampToDisableQueryKeyCache: new Date().getTime(),
      });
    },
    [setQuery],
  );

  return {
    ...chatbotQuery,
    sendChatbotQuery,
  };
};

export const useGlobalChatbotQuery = () => {
  const [query, setQuery] = useState({});
  const { hasQueryContext } = useChatbotQueryContext();

  const {
    chatbotUrl,
    countryCode,
    chatbotBusinessArea: businessArea,
    chatbotChannel: channel,
  } = useAppConfig();

  const chatbotQuery = useQuery({
    queryKey: [CHATBOT_QUERY_KEY, query],
    queryFn: () =>
      fetchChatbotQuery(chatbotUrl, {
        ...query,
        businessArea,
        Channel: channel,
        ...getUserCountryCode(countryCode),
      }),
    enabled: !isEmpty(query) && !hasQueryContext,
    cacheTime: 0,
  });

  const sendGlobalChatbotQuery = useCallback((query) => {
    setQuery({
      ...query,
      timestamp: Date.now(),
    });
  }, []);

  return {
    ...chatbotQuery,
    sendGlobalChatbotQuery,
  };
};
